import Balancer from 'react-wrap-balancer';
import { fraunces } from '../../Landing/fonts';
import { Stamp } from '../../Landing/ui';
import coordinateLine from '../../../helpers/coordinateLine';
import FutureImage from '../../FutureImage';
import AuthorRow from './AuthorRow';
import LocationChip from './LocationChip';
import RouteFlourish from './RouteFlourish';

// UGC titles run up to ~120 characters; step the display size down by
// length so long titles stay inside the scrim. Derived from props only —
// SSR and hydration must agree.
function titleSizeClasses(title) {
  const length = (title || '').length;
  if (length > 90) return 'tw-text-[1.5rem] sm:tw-text-[2.3rem]';
  if (length > 60) return 'tw-text-[1.8rem] sm:tw-text-[2.8rem]';
  return 'tw-text-[2.1rem] sm:tw-text-[3.4rem]';
}

// Cinematic masthead for covers that earned it (wide + high-res, decided
// server-side in helpers/articleHeroMode.js): the photo IS the header,
// edge to edge, with the location chip and title floating on a scrim and
// the warm paper rolling in under an organic curved edge. Overlay inks are
// fixed light colors — the photo doesn't change with the theme, so neither
// may the text on it. This is the LCP element: priority + blur, no
// scroll-coupled effects, fixed svh height (zero CLS).
export default function ArticleMasthead({
  coverImage,
  title,
  location,
  author,
  createdAt,
  readTime,
  permlink,
  thumbnail,
  tags,
  editLink,
}) {
  const coords = coordinateLine(location);

  return (
    <header className="tw-relative tw-mb-2">
      <div className="tf-hero-cinematic tw-relative tw-overflow-hidden">
        <FutureImage
          src={coverImage.url}
          alt={title}
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          className="tw-object-cover"
          placeholder={coverImage.blur ? 'blur' : undefined}
          blurDataURL={coverImage.blur}
        />
        {/* Top scrim: the transparent reading nav sits on the photo */}
        <div
          aria-hidden="true"
          className="tw-absolute tw-inset-x-0 tw-top-0 tw-h-32 tw-bg-gradient-to-b tw-from-[rgba(12,20,17,0.5)] tw-to-transparent"
        />
        {/* Bottom scrim: title legibility on bright or busy covers */}
        <div
          aria-hidden="true"
          className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[68%] tw-bg-gradient-to-t tw-from-[rgba(12,20,17,0.78)] tw-via-[rgba(12,20,17,0.34)] tw-to-transparent"
        />

        <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-mx-auto tw-max-w-4xl tw-px-4 tw-pb-14 tw-text-center sm:tw-pb-[4.5rem]">
          <LocationChip location={location} onPhoto />
          <h1
            className={`${
              fraunces.className
            } ${titleSizeClasses(
              title,
            )} tw-mx-auto tw-mb-0 tw-mt-4 tw-max-w-[22ch] tw-font-semibold tw-leading-[1.08] tw-tracking-tight tw-text-[#F7F4EA] [text-shadow:0_2px_24px_rgba(12,20,17,0.5)]`}
          >
            <Balancer>{title}</Balancer>
          </h1>
        </div>

        {/* Organic paper edge — currentColor so the theme swap is pure CSS */}
        <svg
          aria-hidden="true"
          preserveAspectRatio="none"
          viewBox="0 0 1440 64"
          className="tw-absolute tw-inset-x-0 -tw-bottom-px tw-block tw-h-7 tw-w-full tw-text-[#FAF8F1] dark:tw-text-[#0E1A16] sm:tw-h-11"
        >
          <path
            fill="currentColor"
            d="M0 38 C 240 64, 480 6, 760 18 C 1020 29, 1240 10, 1440 34 L1440 64 L0 64 Z"
          />
        </svg>
      </div>

      {/* Passport stamp straddling the photo/paper seam */}
      <Stamp
        className="tw-absolute tw-right-[6.5%] tw-hidden tw-h-[5.5rem] tw-w-[5.5rem] -tw-translate-y-[55%] tw-rotate-12 tw-text-amber-500/90 sm:tw-block"
        text="TRAVELFEED · FIELD NOTES · "
      />

      <div className="tw-mx-auto tw-max-w-3xl tw-px-4 tw-pb-6 tw-pt-7 tw-text-center">
        <AuthorRow
          author={author}
          createdAt={createdAt}
          readTime={readTime}
          permlink={permlink}
          title={title}
          thumbnail={thumbnail}
          tags={tags}
          editLink={editLink}
        />
        <RouteFlourish className="tw-mt-7" />
        {coords && (
          <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] tw-text-[#8A958F]">
            <span aria-hidden="true">⌖ </span>
            {coords}
          </p>
        )}
      </div>
    </header>
  );
}
